import eventBus from "./events/eventBus";

export interface FetchSuccess<R> {
    status: 'SUCCESS';
    dataType: 'json';
    hasStatusCode: true;
    httpStatusCode: number;
    data: R;
}
export interface FetchTextSuccess {
    status: 'SUCCESS';
    dataType: 'text';
    hasStatusCode: true;
    httpStatusCode: number;
    data: string;
}
export interface FetchSuccessEmpty {
    status: 'SUCCESS';
    dataType: undefined;
    hasStatusCode: true;
    httpStatusCode: number;
}
export interface FetchJsonError<E> {
    status: 'ERROR';
    dataType: 'json';
    hasStatusCode: true;
    httpStatusCode: number;
    data: E;
}
export interface FetchError {
    status: 'ERROR';
    dataType: 'text';
    hasStatusCode: true;
    httpStatusCode: number;
    data: string;
}
export interface FetchException {
    status: 'EXCEPTION';
    dataType: undefined;
    hasStatusCode: false;
    exception: any;
}

export type FetchResponseWithError<R, E = ErrorResponseJson> =
    | FetchSuccess<R>
    | FetchJsonError<E>
    | FetchError
    | FetchException;
export type FetchWithoutReponseWithError<E = ErrorResponseJson> =
    | FetchSuccessEmpty
    | FetchJsonError<E>
    | FetchError
    | FetchException;
export type FetchWithJsonOrTextReponseWithError<R, E = ErrorResponseJson> =
    | FetchSuccess<R>
    | FetchTextSuccess
    | FetchJsonError<E>
    | FetchError
    | FetchException;
export type HttpMethod = 'GET' | 'PUT' | 'POST' | 'DELETE';
export type FetchResponse<T> = T | 'ERROR';
export type UpdateResponse = 'SUCCESS' | 'ERROR';

export interface ErrorResponseJson {
    errors?: ErrorJson[];
}

export interface ErrorJson {
    errorId?: string;
    errorGroup?: string;
    message?: string;
    detailedMessage?: string;
    traceId?: string;
    context?: { [key: string]: string | undefined };
}

export function requestWithResponseData<T>(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<FetchResponse<T>> {
    return getAuthorizationToken()
        .then(authToken => fetchWithAuthorization(url, authToken, method, body))
        .then(response => toJson<T>(response))
        .catch(handleErrors);
}

export function requestUnauthenticatedWithResponseData<T>(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<FetchResponse<T>> {
    return fetchWithoutAuthorization(url, method, body)
        .then(response => toJson<T>(response))
        .catch(handleErrors);
}

export function requestWithoutResponseData(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<UpdateResponse> {
    return getAuthorizationToken()
        .then(authToken => fetchWithAuthorization(url, authToken, method, body))
        .then(toUpdateResponse)
        .catch(handleErrors);
}

export function requestWithoutResponseWithErrorHandlingWithoutToken<E = any>(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<FetchWithoutReponseWithError<E>> {
    return fetchWithoutAuthorization(url, method, body)
        .then(response => extractStatusCodeAndEmptyBody<E>(response))
        .catch((exception: any): FetchWithoutReponseWithError<E> => {
            return {
                status: 'EXCEPTION',
                dataType: undefined,
                hasStatusCode: false,
                exception,
            };
        });
}

export function requestWithErrorHandlingWithoutToken<R, E = any>(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<FetchWithJsonOrTextReponseWithError<R, E>> {
    return fetchWithoutAuthorization(url, method, body)
        .then(response => extractStatusCodeAndMaybeResponse<R, E>(response))
        .catch((exception: any): FetchWithJsonOrTextReponseWithError<R, E> => {
            return {
                status: 'EXCEPTION',
                dataType: undefined,
                hasStatusCode: false,
                exception,
            };
        });
}

export function requestWithErrorHandlingWithToken<R, E = any>(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<FetchWithJsonOrTextReponseWithError<R, E>> {
    return getAuthorizationToken()
        .then(authToken => fetchWithAuthorization(url, authToken, method, body))
        .then(response => extractStatusCodeAndMaybeResponse<R, E>(response))
        .catch((exception: any): FetchWithJsonOrTextReponseWithError<R, E> => {
            return {
                status: 'EXCEPTION',
                dataType: undefined,
                hasStatusCode: false,
                exception,
            };
        });
}

export function getAuthorizationToken(): Promise<string> {
    const token = localStorage.getItem('JWT_TOKEN_2');
    if (token) {
        return Promise.resolve(token);
    } else {
        eventBus.dispatch('clearUser');
        return Promise.reject('No user logged in');
    }
}

function fetchWithoutAuthorization(
    url: string,
    method: HttpMethod,
    body?: string,
): Promise<Response> {
    return fetch(url, {
        headers: {
            ...(body && { 'content-type': 'application/json' }),
        },
        method,
        body,
    });
}

function fetchWithAuthorization(
    url: string,
    authToken: string,
    method: HttpMethod,
    body?: string,
): Promise<Response> {
    let responsePromise = fetch(url, {
        headers: {
            Authorization: 'Bearer ' + authToken,
            ...(body && { 'content-type': 'application/json' }),
        },
        method,
        body,
    });
    return handleAuthError(responsePromise);
}

function extractStatusCodeAndBody<R, E = ErrorResponseJson>(
    response: Response,
): Promise<FetchResponseWithError<R, E>> {
    if (response.ok) {
        return response.json().then((json: R) => {
            return {
                status: 'SUCCESS',
                hasStatusCode: true,
                httpStatusCode: response.status,
                dataType: 'json',
                data: json,
            };
        });
    } else {
        return response.text().then((text: string) => {
            try {
                const json = JSON.parse(text) as E;
                return {
                    status: 'ERROR',
                    errorType: 'json',
                    hasStatusCode: true,
                    httpStatusCode: response.status,
                    dataType: 'json',
                    data: json,
                };
            } catch (error) {
                return {
                    status: 'ERROR',
                    errorType: 'text',
                    hasStatusCode: true,
                    httpStatusCode: response.status,
                    dataType: 'text',
                    data: text,
                };
            }
        });
    }
}

function extractStatusCodeAndEmptyBody<E = ErrorResponseJson>(
    response: Response,
): Promise<FetchWithoutReponseWithError<E>> {
    if (response.ok) {
        return Promise.resolve({
            status: 'SUCCESS',
            hasStatusCode: true,
            httpStatusCode: response.status,
            dataType: undefined,
        });
    } else {
        return response.text().then((text: string) => {
            try {
                const json = JSON.parse(text) as E;
                return {
                    status: 'ERROR',
                    errorType: 'json',
                    hasStatusCode: true,
                    httpStatusCode: response.status,
                    dataType: 'json',
                    data: json,
                };
            } catch (error) {
                return {
                    status: 'ERROR',
                    errorType: 'text',
                    hasStatusCode: true,
                    httpStatusCode: response.status,
                    dataType: 'text',
                    data: text,
                };
            }
        });
    }
}

function extractStatusCodeAndMaybeResponse<R, E = ErrorResponseJson>(
    response: Response,
): Promise<FetchWithJsonOrTextReponseWithError<R, E>> {
    return response.text().then((text: string) => {
        try {
            if (response.ok) {
                const json = JSON.parse(text) as R;
                return {
                    status: 'SUCCESS',
                    hasStatusCode: true,
                    httpStatusCode: response.status,
                    dataType: 'json',
                    data: json,
                };
            } else {
                const json = JSON.parse(text) as E;
                return {
                    status: 'ERROR',
                    hasStatusCode: true,
                    httpStatusCode: response.status,
                    dataType: 'json',
                    data: json,
                };
            }
        } catch (error) {
            const status = response.ok ? 'SUCCESS' : 'ERROR';
            return {
                status,
                hasStatusCode: true,
                httpStatusCode: response.status,
                dataType: 'text',
                data: text,
            };
        }
    });
}

function handleAuthError(responsePromise: Promise<Response>): Promise<Response> {
    return responsePromise.then(response => {
        if (response.status == 403) {
            eventBus.dispatch('clearUser');
        }
        return Promise.resolve(response);
    });
}

function toJson<T>(response: Response): Promise<T> {
    if (response.ok) {
        return response.json();
    }
    return Promise.reject();
}

function toUpdateResponse(response: Response): UpdateResponse {
    if (response.ok) {
        return 'SUCCESS';
    } else {
        return 'ERROR';
    }
}

function handleErrors(): 'ERROR' {
    return 'ERROR';
}
